import { makeAutoObservable, runInAction } from 'mobx'
import { createContext } from 'react'
import I18n from '../../shared-lib/i18n/i18n'
import languages from '../../languages'
import ErrorHandler from '../lib/ErrorHandler'
import Communication from '../../shared-lib/communication/Communication'
import UIStore from './UIStore/UIStore'
import { getBrowserTimezone } from '../../shared-lib/utils'
import NavigationStore from './NavigationStore'
import AuthStore from '../../shared-solo/stores/AuthStore/AuthStore'
import SalonStore from '../../shared-solo/stores/SalonStore/SalonStore'
import UserStore from '../../shared-solo/stores/UserStore/UserStore'
import StoreDataSync from '../../shared-lib/lib/StoreDataSync'
import analytics from './analytics'
import ExternalClientStore from '../../shared-solo/stores/ExternalClientStore/ExternalClientStore'
import ExternalClientModel from '../../shared-solo/stores/ExternalClientStore/ExternalClientModel'
import ExternalBookingStore from '../../shared-solo/stores/ExternalBookingStore/ExternalBookingStore'
import ExternalBookingModel from '../../shared-solo/stores/ExternalBookingStore/ExternalBookingModel'
import BookingStore from '../../shared-solo/stores/BookingStore/BookingStore'
import BookingModel from '../../shared-solo/stores/BookingStore/BookingModel'
import DropdownMessageHandler from './DropdownMessageHandler'
import ExternalAddressStore from '../../shared-solo/stores/ExternalAddressStore/ExternalAddressStore'
import ExternalAddressModel from '../../shared-solo/stores/ExternalAddressStore/ExternalAddressModel'
import ExternalTreatmentLibrary from 'shared-solo/stores/ExternalTreatmentStore/ExternalTreatmentLibrary'
import ExternalTreatmentStore from 'shared-solo/stores/ExternalTreatmentStore/ExternalTreatmentStore'
import ConsultationFormStore from 'shared-solo/stores/ConsultationFormStore/ConsultationFormStore'
import ExternalTreatmentModel from 'shared-solo/stores/ExternalTreatmentStore/ExternalTreatmentModel'
import ConsultationFormModel from 'shared-solo/stores/ConsultationFormStore/ConsultationFormModel'
import UserDeviceStore from './UserDeviceStore'
import ExternalReviewStore from '../../shared-solo/stores/ExternalReviewStore/ExternalReviewStore'
import AgendaStore from '../../shared-solo/stores/AgendaStore/AgendaStore'
import ExternalPromocodeStore from '../../shared-solo/stores/ExternalPromocodeStore/ExternalPromocodeStore'
import ExternalGiftCardStore from '../../shared-solo/stores/ExternalGiftCardStore/ExternalGiftCardStore'
import ExternalPromocodeModel from '../../shared-solo/stores/ExternalPromocodeStore/ExternalPromocodeModel'
import ExternalReviewModel from '../../shared-solo/stores/ExternalReviewStore/ExternalReviewModel'
import ExternalGiftCardModel from '../../shared-solo/stores/ExternalGiftCardStore/ExternalGiftCardModel'
import SubscriptionStore from './SubscriptionStore'
import BottomMessageHandler from './BottomMessageHandler'
import ProfessionalAvailabilityStore from 'shared-solo/stores/ProfessionalAvailabilityStore/ProfessionalAvailabilityStore'
import ImageModalHandler from './ImageModalHandler'
import { SearchStore } from './SearchStore/SearchStore'
import StatisticsStore from '../../shared-solo/stores/StatisticsStore/StatisticsStore'
import CrmStore from 'shared-solo/stores/CrmStore/CrmStore'
import EmailCampaignStore from 'shared-solo/stores/EmailCampaignStore/EmailCampaignStore'
import EmailCampaignModel from 'shared-solo/stores/EmailCampaignStore/EmailCampaignModel'
import SmsCampaignStore from 'shared-solo/stores/SmsCampaignStore/SmsCampaignStore'
import SmsCampaignModel from 'shared-solo/stores/SmsCampaignStore/SmsCampaignModel'
import { EmailPreviewHelper } from '../../shared-solo/lib/EmailPreviewHelper'
import SetupStore from './SetupStore/SetupStore'
import WebPageStore from '../../shared-solo/stores/WebPageStore/WebPageStore'
import { NavigateFunction } from 'react-router'
import WebPageModel from 'shared-solo/stores/WebPageStore/WebPageModel'
import EditTextModalController from './EditTextModalController'
import GoogleBusinessProfileStore from 'shared-solo/stores/GoogleBusinessProfileStore/GoogleBusinessProfileStore'
import SearchManifestStore from 'shared-solo/stores/SearchManifestStore/SearchManifestStore'
import { LANDING_ROUTES, routeAllowedBy } from '~/lib/routeAccess'
import {
  applyConfigResponse,
  clearGraphQLCache,
  clearSharedStores,
  loadSharedSession,
  openCommunication,
} from 'shared-solo/lib/rootStoreLifecycle'
import {
  SessionAccess,
  SessionCapability,
  allowedBy,
  hasCapability,
  landingFor,
  sessionAccessOf,
} from 'shared-solo/lib/sessionCapabilities'

const GLOBAL_ENV = {
  SS_ENV: import.meta.env.VITE_SS_ENV,
  API_DEFAULT: import.meta.env.VITE_API_DEFAULT,
  PLATFORM: import.meta.env.VITE_PLATFORM,
  APP_VERSION: '0.0.0', // TODO GET FROM PACKAGE
  API_OVERRIDE_URL: import.meta.env.VITE_API_OVERRIDE_URL,
  USE_SECURE: import.meta.env.VITE_USE_SECURE,
  ADD_NETWORK_LATENCY: '0',
  STORAGE_KEY: import.meta.env.VITE_STORAGE_KEY,
  SEGMENT_WRITE_KEY: import.meta.env.VITE_SEGMENT_WRITE_KEY,
  SW_DEFAULT: import.meta.env.VITE_SW_DEFAULT,
  DEEP_LINKING_URL: import.meta.env.VITE_DEEP_LINKING_URL,
  IMAGES_LOCATION: import.meta.env.VITE_IMAGES_LOCATION,
  IMAGE_KIT_URL: import.meta.env.VITE_IMAGE_KIT_URL,
  GROWTH_HUB_SECRET_TOKEN: import.meta.env.VITE_GROWTH_HUB_SECRET_TOKEN || 'secretToken',
  SEARCH_MANIFEST_URL: import.meta.env.VITE_SEARCH_MANIFEST_URL,
}

export const platformOptions = {}
type Config = {
  name: string
  platform: 'PA' | 'SD',
  platformOS: 'web',
  deepLinkingUrl: string
  bookingPortalUrl: string
  apiDefault: string
  useSecure: boolean
  imagesLocation: string
  imageKitUrl: string
  addressSearchCountry?: string
  plan?: string
  timezone: string
  testEmailAddress?: string
  growthHubSecretToken: string
  searchManifestUrl?: string
}

type RemoteConfigProperties = {
  costForFeature?: Record<string, any>
  openedCountries?: string[]
  daysOfFreeTrial?: number
  defaultBookingLimit?: number
  quickUsers?: { name: string, number: string }[]
  planNames?: Record<string, string>
}

class RootStore {
  appVer: { apiVersion: string | null } = { apiVersion: null }
  ready = false
  canAccessTTP = false
  communication!: Communication
  i18n: I18n
  // _router:
  config: Config
  uiStore: UIStore
  externalClientStore: ExternalClientStore
  externalBookingStore: ExternalBookingStore
  bookingStore: BookingStore
  navigationStore: NavigationStore
  externalAddressStore: ExternalAddressStore
  externalPromocodeStore: ExternalPromocodeStore
  storeDataSync: StoreDataSync
  externalTreatmentLibrary: ExternalTreatmentLibrary
  externalTreatmentStore: ExternalTreatmentStore
  consultationFormStore: ConsultationFormStore
  userDeviceStore: UserDeviceStore
  agendaStore: AgendaStore
  externalReviewStore: ExternalReviewStore
  externalGiftCardStore: ExternalGiftCardStore
  subscriptionStore: SubscriptionStore
  searchStore: SearchStore
  professionalAvailabilityStore: ProfessionalAvailabilityStore
  emailPreviewHelper: EmailPreviewHelper
  userStore: UserStore
  authStore: AuthStore
  salonStore: SalonStore
  errorHandler: ErrorHandler
  dropdownMessageHandler: DropdownMessageHandler
  bottomMessageHandler: BottomMessageHandler
  pathname = ''
  remoteConfig: RemoteConfigProperties = {}
  imageModalHandler: ImageModalHandler
  statisticsStore: StatisticsStore
  crmStore: CrmStore
  emailCampaignStore: EmailCampaignStore
  smsCampaignStore: SmsCampaignStore
  setupStore: SetupStore
  webPageStore: WebPageStore
  editTextModalController: EditTextModalController
  googleBusinessProfileStore: GoogleBusinessProfileStore
  searchManifestStore: SearchManifestStore
  constructor() {
    this.config = {
      name: GLOBAL_ENV.SS_ENV,
      platform: GLOBAL_ENV.PLATFORM,
      platformOS: 'web',
      bookingPortalUrl: GLOBAL_ENV.SW_DEFAULT,
      deepLinkingUrl: GLOBAL_ENV.DEEP_LINKING_URL,
      imagesLocation: GLOBAL_ENV.IMAGES_LOCATION,
      imageKitUrl: GLOBAL_ENV.IMAGE_KIT_URL,
      apiDefault: GLOBAL_ENV.API_DEFAULT,
      useSecure: !!GLOBAL_ENV.USE_SECURE,
      timezone: getBrowserTimezone(),
      growthHubSecretToken: GLOBAL_ENV.GROWTH_HUB_SECRET_TOKEN,
      searchManifestUrl: GLOBAL_ENV.SEARCH_MANIFEST_URL || undefined,
    }
    this.i18n = new I18n(languages, 'en')
    this.errorHandler = new ErrorHandler(this)
    this.uiStore = new UIStore(this)
    this.navigationStore = new NavigationStore(this)
    this.authStore = new AuthStore(this)
    this.salonStore = new SalonStore(this)
    this.userStore = new UserStore({}, this)
    this.dropdownMessageHandler = new DropdownMessageHandler()
    this.bottomMessageHandler = new BottomMessageHandler()
    this.imageModalHandler = new ImageModalHandler()
    this.externalClientStore = new ExternalClientStore(this, ExternalClientModel)
    this.externalBookingStore = new ExternalBookingStore(this, ExternalBookingModel)
    this.bookingStore = new BookingStore(this, BookingModel)
    this.externalAddressStore = new ExternalAddressStore(this, ExternalAddressModel)
    this.externalTreatmentLibrary = new ExternalTreatmentLibrary(this, ExternalTreatmentModel)
    this.externalTreatmentStore = new ExternalTreatmentStore(this, ExternalTreatmentModel)
    this.consultationFormStore = new ConsultationFormStore(this, ConsultationFormModel)
    this.userDeviceStore = new UserDeviceStore({}, this)
    this.agendaStore = new AgendaStore(this)
    this.professionalAvailabilityStore = new ProfessionalAvailabilityStore(this)
    this.externalReviewStore = new ExternalReviewStore(this, ExternalReviewModel)
    this.externalPromocodeStore = new ExternalPromocodeStore(this, ExternalPromocodeModel)
    this.externalGiftCardStore = new ExternalGiftCardStore(this, ExternalGiftCardModel)
    this.subscriptionStore = new SubscriptionStore(this)
    this.searchStore = new SearchStore(this)
    this.statisticsStore = new StatisticsStore(this)
    this.storeDataSync = new StoreDataSync(GLOBAL_ENV.STORAGE_KEY)
    this.emailCampaignStore = new EmailCampaignStore(this, EmailCampaignModel)
    this.smsCampaignStore = new SmsCampaignStore(this, SmsCampaignModel)
    this.crmStore = new CrmStore(this)
    this.emailPreviewHelper = new EmailPreviewHelper(this)
    this.setupStore = new SetupStore(this)
    this.webPageStore = new WebPageStore(this, WebPageModel)
    this.editTextModalController = new EditTextModalController(this)
    this.googleBusinessProfileStore = new GoogleBusinessProfileStore(this)
    this.searchManifestStore = new SearchManifestStore(this)
    this.storeDataSync.addSyncStore('AUTH', this.authStore)
    this.storeDataSync.addSyncStore('USER', this.userStore)
    this.storeDataSync.addSyncStore('UI', this.uiStore)
    this.storeDataSync.addSyncStore('SEARCH', this.searchStore)
    analytics.init()

    makeAutoObservable(this)
  }

  loadStore = async () => {
    const communicationLoaded = await this.loadCommunication({
      apiDefault: GLOBAL_ENV.API_DEFAULT,
      platform: GLOBAL_ENV.PLATFORM,
      timezone: getBrowserTimezone(),
      env: GLOBAL_ENV.SS_ENV,
      appVersion: GLOBAL_ENV.APP_VERSION,
      override: GLOBAL_ENV.API_OVERRIDE_URL,
      useSecure: GLOBAL_ENV.USE_SECURE,
    })

    if (!communicationLoaded) {
      this.uiStore.showNetworkError()
      return
    }

    this.storeDataSync.setStorage(localStorage)
    await this.storeDataSync.inflateStoreFromStorage('AUTH')
    await this.storeDataSync.inflateStoreFromStorage('UI')
    await this.storeDataSync.inflateStoreFromStorage('USER')
    await this.storeDataSync.inflateStoreFromStorage('SEARCH')

    analytics.track('App Loaded')

    if (this.authStore.isLoggedIn) {
      await this.afterAuthenticated()
    }

    await this.getConfig_api()
    this.setIsReady(true)
    this.searchManifestStore.refreshInBackground()
  }

  afterAuthenticated = async () => {
    const { isReceptionSession } = this.authStore

    await clearGraphQLCache(this)
    await loadSharedSession(this)

    this.externalBookingStore.listenOnExternalBookings_api()
    this.userStore.listenOnUserUpdated_api()
    this.errorHandler.setUserContext(this.userStore.data)

    if (!isReceptionSession) {
      // Foce a sync with Chargebee
      this.subscriptionStore.syncWithChargebee()
    }
  }

  loadCommunication = async (config: Record<string, string>) => {
    const opened = await openCommunication(this, {
      apiDefault: config.apiDefault,
      platform: config.platform,
      appVersion: config.appVersion,
      useSecure: config.useSecure || (config.env === 'development' ? '' : 's'),
      override: config.override,
      timezone: config.timezone,
      env: config.env,
      addNetworkLatency: parseInt(GLOBAL_ENV.ADD_NETWORK_LATENCY || '0'),
      resubscribeActiveListeners: async () => {
        await this.externalBookingStore.listenOnExternalBookings_api()
        await this.userStore.listenOnUserUpdated_api()
        await this.salonStore.listenOnSalonMemberUpdated_api()
      },
    })

    if (!opened) return false

    runInAction(() => {
      this.communication = opened.communication
    })

    return true
  }

  getConfig_api = async (): Promise<boolean> => {
    const { s } = this.i18n
    const { requester } = this.communication

    try {
      const ret = await requester.getConfig()

      // appVer is only needed on mobile.
      return applyConfigResponse(this, ret, {
        requireAppVer: false,
        onFailure: () => this.uiStore.showMessage({
          title: s('Something went wrong'),
          body: s('Something went wrong contacting our server. Please try again.'),
          buttons: [
            { title: s('Retry'), onPress: this.getConfig_api },
          ],
        }),
      })
    } catch (e) {
      return false
    }
  }

  setIsReady = (ready: boolean) => {
    this.ready = ready
  }

  authRequired = (navigate: NavigateFunction) => {
    if (!this.authStore.isLoggedIn || !!this.authStore.sessionExpired) {
      const redirectRoute = encodeURIComponent(window.location.pathname + window.location.search)
      navigate(`/auth/login?r=${redirectRoute}`)
      return false
    }

    return true
  }

  isPublicRoute = (pathname: string) => {
    return pathname.startsWith('/auth/')
      || pathname.startsWith('/deep-link')
      || pathname.startsWith('/setup/google-business-profile')
  }

  get isDevOrStaging(): boolean {
    return this.config.name === 'development' || this.config.name === 'staging'
  }

  get sessionAccess(): SessionAccess {
    return sessionAccessOf(this)
  }

  get landingPath(): string {
    return landingFor(this.sessionAccess, LANDING_ROUTES)
  }

  canAccessFeature = (capability: SessionCapability): boolean => hasCapability(this.sessionAccess, capability)

  canAccessRoute = (pathname: string): boolean =>
    allowedBy(this.sessionAccess, capabilities => routeAllowedBy(capabilities, pathname, this.isDevOrStaging))

  checkAuth = (pathname: string, navigate: NavigateFunction) => {
    if (this.isPublicRoute(pathname)) return
    if (!this.authRequired(navigate)) return

    if (!this.canAccessRoute(pathname)) {
      navigate(this.landingPath, { replace: true })
    }
  }

  clearAllData = () => {
    clearSharedStores(this)
    this.uiStore.clearAccountData()
    this.searchStore.clearData()
  }
}

export type $RootStore = RootStore
export const __store = new RootStore()
export default createContext(__store)